import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popper,
  PopperProps,
  TextField,
  Typography
} from "@mui/material";

/* region Languages */
import { CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
/* endregion Languages */

import { compareVersions, linesBetween, versionLine } from "../changelog";
import { fill } from "./ChangelogVersionView";
import { MAX_RANGE_LINES } from "./versionDetails";

/** Keeps the version list below the field, even when the dialog sits low on the screen */
const BottomPopper: React.FC<PopperProps> = (props) => (
  <Popper {...props} placement="bottom-start" modifiers={[{ name: "flip", enabled: false }]} />
);

interface VersionRangeDialogProps {
  open: boolean;
  /** Every version in the CHANGELOG, newest first */
  versions: string[];
  /** Version selected in Novidades, used as the initial interval */
  initial: string;
  onClose: () => void;
  /** Called with the interval in ascending order */
  onConfirm: (from: string, to: string) => void;
}

/** Picks an interval of versions, at most MAX_RANGE_LINES version lines wide, for the summary of versions */
const VersionRangeDialog: React.FC<VersionRangeDialogProps> = ({ open, versions, initial, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [from, setFrom] = useState(initial);
  const [to, setTo] = useState(initial);

  useEffect(() => {
    if (open) {
      setFrom(initial);
      setTo(initial);
    }
  }, [open, initial]);

  const [low, high] = compareVersions(from, to) <= 0 ? [from, to] : [to, from];
  const lines = linesBetween(versions, low, high);
  const tooWide = lines.length > MAX_RANGE_LINES;

  // A text field that filters the versions as you type, with the list grouped by version line
  const field = (label: string, value: string, onChange: (version: string) => void) => (
    <Autocomplete
      options={versions}
      value={value}
      onChange={(_, version) => version && onChange(version)}
      groupBy={(version) => fill(t(CustomerMessages.docsVersionLine), { LINE: versionLine(version) })}
      disableClearable
      openOnFocus
      autoHighlight
      noOptionsText={t(CustomerMessages.docsVersionNoOptions)}
      PopperComponent={BottomPopper}
      ListboxProps={{ style: { maxHeight: 260 } }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={t(CustomerMessages.docsVersionFilter)} size="small" />
      )}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="version-range-title">
      <DialogTitle id="version-range-title">{t(CustomerMessages.docsVersionRangeDialogTitle)}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          {fill(t(CustomerMessages.docsVersionRangeHelp), { MAX: MAX_RANGE_LINES })}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 2 }}>
          {field(t(CustomerMessages.docsVersionFrom), from, setFrom)}
          {field(t(CustomerMessages.docsVersionTo), to, setTo)}
        </Box>
        {tooWide && (
          <Typography role="alert" sx={{ fontSize: 13, color: "error.main", mt: 1.5 }}>
            {fill(t(CustomerMessages.docsVersionRangeTooWide), { COUNT: lines.length, LINES: lines.join(", "), MAX: MAX_RANGE_LINES })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(CustomerMessages.docsCancel)}</Button>
        <Button variant="contained" disabled={tooWide} onClick={() => onConfirm(low, high)}>
          {t(CustomerMessages.docsVersionOpenRange)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionRangeDialog;
